import React from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { formatDate as formatDateGlobal, formatDateTime as formatDateTimeGlobal } from '../../config/appConfig';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  CalendarIcon,
  ChevronLeftIcon,
  ClockIcon,
  DateInput,
  DocumentTextIcon,
  EyeIcon,
  Label,
  NumberInput,
  PlusIcon,
  Select,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../components/ui';

import {
  savePublicRfqDraft,
  submitPublicRfqOffer,
  type PublicRfqCurrencyOption,
  type PublicRfqUnitOption,
} from '../../services/publicRfqApi';
import type {
  SupplierQuotation,
  SupplierQuotationExpense,
  SupplierQuotationLine,
} from '../supplies/supplier-quotations/useData';
import { formatCaseParenthetical } from '../supplies/requisitions/caseDisplay';

type LineForm = {
  key: string;
  level1Filter: string;
  itemId: string;
  itemVcode: string;
  itemDescr: string;
  unitId: string;
  qty: string;
  requisitionQty: string;
  enquiryQty: string;
  unitPrice: string;
  unitTotal: string;
  lineTotal: string;
  discount: string;
  vat: string;
  itemNotes: string;
};

type ExpenseForm = {
  key: string;
  description: string;
  value: string;
  vat: string;
};

const newExpense = (): ExpenseForm => ({
  key: `exp-${Date.now()}-${Math.random()}`,
  description: '',
  value: '',
  vat: '',
});

const asNumber = (value: string): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const toFixed2String = (value: number): string => value.toFixed(2);

const computeUnitTotal = (qty: string, unitPrice: string): string => {
  const q = Number(qty);
  const p = Number(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '';
  const total = q * p;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const computeFinalTotal = (unitTotal: string, discount: string, vat: string): string => {
  const u = Number(unitTotal);
  const d = Number(discount || '0');
  const v = Number(vat || '0');
  if (!Number.isFinite(u) || !Number.isFinite(d) || !Number.isFinite(v)) return '';
  const total = u - d + v;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const computeCurrencyTotal = (lineTotal: string, rate: string): string => {
  const t = Number(lineTotal);
  const r = Number(rate);
  if (!Number.isFinite(t) || !Number.isFinite(r)) return '';
  const total = t * r;
  if (!Number.isFinite(total)) return '';
  return toFixed2String(total);
};

const asFinite = (value: string): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (n: number): string =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function formatQuotationItemLabel(
  vendorCode: string | null | undefined,
  name: string | null | undefined,
  unitId: string | number | null | undefined,
  units: { id: number; ucode?: string | null }[],
  unitUcodeFromItem?: string | null,
): string {
  const v = (vendorCode ?? '').trim() || '';
  const n = (name ?? '').trim() || '';
  let ucode = (unitUcodeFromItem ?? '').trim();
  if (!ucode && unitId != null && unitId !== '') {
    const uid = typeof unitId === 'string' ? Number(unitId) : unitId;
    if (typeof uid === 'number' && !Number.isNaN(uid)) {
      ucode = units.find((x) => x.id === uid)?.ucode?.trim() ?? '';
    }
  }
  return ucode ? `${v} - ${n} - (${ucode})` : `${v} - ${n}`;
}

export type PublicRfqQuotationEditorProps = {
  token: string;
  pin: string;
  quotation: SupplierQuotation;
  currencies: PublicRfqCurrencyOption[];
  units: PublicRfqUnitOption[];
  onBack: () => void;
  onOfferSubmitted: () => void;
  onQuotationUpdated: (q: SupplierQuotation) => void;
};

const PublicRfqQuotationEditor: React.FC<PublicRfqQuotationEditorProps> = ({
  token,
  pin,
  quotation: initialQuotation,
  currencies,
  units,
  onBack,
  onOfferSubmitted,
  onQuotationUpdated,
}) => {
  const { colors } = useTheme();
  const [quotation, setQuotation] = React.useState(initialQuotation);
  React.useEffect(() => {
    setQuotation(initialQuotation);
  }, [initialQuotation]);

  const [formData, setFormData] = React.useState({
    quoDate: '',
    ref: '',
    notes: '',
    supplierNotes: '',
    rate: '',
    paymentTerms: '',
    supplierRef: '',
    currencyId: '',
  });
  const [lines, setLines] = React.useState<LineForm[]>([]);
  const [expenses, setExpenses] = React.useState<ExpenseForm[]>([newExpense()]);
  const [bulkDiscountPct, setBulkDiscountPct] = React.useState('');
  const [bulkVatPct, setBulkVatPct] = React.useState('');
  const [draftBusy, setDraftBusy] = React.useState(false);
  const [submitBusy, setSubmitBusy] = React.useState(false);

  React.useEffect(() => {
    const existing = quotation;
    setFormData({
      quoDate: existing.quoDate ? existing.quoDate.slice(0, 10) : '',
      ref: existing.ref || '',
      notes: existing.notes || '',
      supplierNotes: existing.supplierNotes || '',
      rate: existing.rate != null ? String(existing.rate) : '',
      paymentTerms: existing.paymentTerms || '',
      supplierRef: existing.supplierRef || '',
      currencyId: existing.currencyId != null ? String(existing.currencyId) : '',
    });
    const mapped = (existing.lineItems || []).map((line) => ({
      key: `line-${line.id ?? Math.random()}`,
      level1Filter: (line.item?.level1 ?? '').trim(),
      itemId: line.itemId ? String(line.itemId) : '',
      itemVcode: line.itemVcode || '',
      itemDescr: line.itemDescr || '',
      unitId: line.unitId ? String(line.unitId) : '',
      qty: line.qty != null ? String(line.qty) : '',
      requisitionQty: line.requisitionQty != null ? String(line.requisitionQty) : '',
      enquiryQty: line.enquiryQty != null ? String(line.enquiryQty) : '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      unitTotal: computeUnitTotal(line.qty != null ? String(line.qty) : '', line.unitPrice != null ? String(line.unitPrice) : ''),
      lineTotal:
        line.lineTotal != null
          ? String(line.lineTotal)
          : computeFinalTotal(
              computeUnitTotal(
                line.qty != null ? String(line.qty) : '',
                line.unitPrice != null ? String(line.unitPrice) : '',
              ),
              line.discount != null ? String(line.discount) : '',
              line.vat != null ? String(line.vat) : '',
            ),
      discount: line.discount != null ? String(line.discount) : '',
      vat: line.vat != null ? String(line.vat) : '',
      itemNotes: line.itemNotes || '',
    }));
    setLines(mapped);
    const expMapped = (existing.expenses || []).map((exp) => ({
      key: `exp-${exp.id ?? Math.random()}`,
      description: exp.description || '',
      value: exp.value != null ? String(exp.value) : '',
      vat: exp.vat != null ? String(exp.vat) : '',
    }));
    setExpenses(expMapped.length > 0 ? expMapped : [newExpense()]);
  }, [quotation]);

  const currencyOptions = React.useMemo(() => {
    const rows = currencies.map((c) => ({
      value: String(c.id),
      label: c.name,
    }));
    if (
      quotation.currencyId &&
      quotation.currency &&
      !rows.some((o) => o.value === String(quotation.currencyId))
    ) {
      rows.push({
        value: String(quotation.currencyId),
        label: quotation.currency.name,
      });
    }
    return [{ value: '', label: 'Currency (optional)' }, ...rows];
  }, [currencies, quotation.currency, quotation.currencyId]);

  const currencyTotalCaption = React.useMemo(() => {
    const selectedId = formData.currencyId ? Number(formData.currencyId) : NaN;
    const selected = Number.isFinite(selectedId) ? currencies.find((c) => c.id === selectedId) : undefined;
    const code = (selected?.code ?? quotation.currency?.code ?? '').trim();
    return code ? `(${code}) Total` : 'Total';
  }, [currencies, formData.currencyId, quotation.currency]);

  const lineSums = React.useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        acc.unitTotal += asFinite(line.unitTotal);
        acc.discount += asFinite(line.discount);
        acc.vat += asFinite(line.vat);
        acc.total += asFinite(line.lineTotal);
        acc.currencyTotal += asFinite(computeCurrencyTotal(line.lineTotal, formData.rate));
        return acc;
      },
      { unitTotal: 0, discount: 0, vat: 0, total: 0, currencyTotal: 0 },
    );
  }, [lines, formData.rate]);

  const expenseSums = React.useMemo(() => {
    const rate = asFinite(formData.rate);
    return expenses.reduce(
      (acc, row) => {
        const val = asFinite(row.value);
        const vt = asFinite(row.vat);
        acc.value += val;
        acc.vat += vt;
        acc.currencyTotal += (val + vt) * rate;
        return acc;
      },
      { value: 0, vat: 0, currencyTotal: 0 },
    );
  }, [expenses, formData.rate]);

  const quoteCcySubtotal = lineSums.total + expenseSums.value + expenseSums.vat;
  const grandCurrencyTotal = lineSums.currencyTotal + expenseSums.currencyTotal;

  const normalizedLines: SupplierQuotationLine[] = React.useMemo(() => {
    return lines
      .map((line) => ({
        itemId: asNumber(line.itemId),
        itemVcode: line.itemVcode || undefined,
        itemDescr: line.itemDescr || undefined,
        unitId: asNumber(line.unitId),
        qty: asNumber(line.qty),
        unitPrice: asNumber(line.unitPrice),
        lineTotal: asNumber(line.lineTotal),
        discount: asNumber(line.discount),
        vat: asNumber(line.vat),
        requisitionQty: asNumber(line.requisitionQty),
        enquiryQty: asNumber(line.enquiryQty),
        itemNotes: line.itemNotes || undefined,
      }))
      .filter(
        (line) =>
          line.itemId ||
          line.itemDescr ||
          line.qty ||
          line.itemNotes ||
          line.discount != null ||
          line.vat != null,
      );
  }, [lines]);

  const normalizedExpenses: SupplierQuotationExpense[] = React.useMemo(() => {
    return expenses
      .map((row) => ({
        description: row.description.trim() || undefined,
        value: asNumber(row.value),
        vat: asNumber(row.vat),
      }))
      .filter((row) => row.description || row.value != null || row.vat != null);
  }, [expenses]);

  const lineRollCount = lines.length > 0;
  const expenseRollCount = normalizedExpenses.length > 0;

  const buildQuotationPatch = React.useCallback(() => {
    return {
      quoDate: formData.quoDate || undefined,
      enqId: quotation.enqId,
      ref: formData.ref || undefined,
      notes: formData.notes || undefined,
      rate: asNumber(formData.rate),
      paymentTerms: formData.paymentTerms || undefined,
      supplierRef: formData.supplierRef || undefined,
      supplierNotes: formData.supplierNotes || undefined,
      currencyId: asNumber(formData.currencyId),
      lineItems: normalizedLines,
      expenses: normalizedExpenses,
    };
  }, [
    formData,
    quotation.enqId,
    normalizedLines,
    normalizedExpenses,
  ]);

  const persistDraft = async () => {
    setDraftBusy(true);
    try {
      const updated = await savePublicRfqDraft(token, pin, buildQuotationPatch());
      setQuotation(updated);
      onQuotationUpdated(updated);
      toast.success('Draft saved.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save draft.');
    } finally {
      setDraftBusy(false);
    }
  };

  const finalizeOffer = async () => {
    setSubmitBusy(true);
    try {
      await submitPublicRfqOffer(token, pin, buildQuotationPatch());
      toast.success('Your offer has been submitted. Thank you.');
      onOfferSubmitted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not submit offer.');
    } finally {
      setSubmitBusy(false);
    }
  };

  const quotationCaseCaption = React.useMemo(() => {
    const caseIdN = quotation.caseId ?? null;
    if (caseIdN == null || caseIdN <= 0) return null;
    return (
      formatCaseParenthetical(
        quotation.supplyCase ?? { id: caseIdN },
        (iso) => formatDateGlobal(iso || undefined),
      ) ?? null
    );
  }, [quotation.caseId, quotation.supplyCase]);

  const quotationEnquiryCaption = React.useMemo(() => {
    const ref = quotation.enquiry?.ref?.trim() || (quotation.enqId != null ? `ENQ-${quotation.enqId}` : '');
    if (!ref) return null;
    const date = quotation.enquiry?.enqDate ? formatDateGlobal(quotation.enquiry.enqDate) : '';
    return `Enquiry: ${ref}${date ? ` - ${date}` : ''}`;
  }, [quotation.enquiry?.ref, quotation.enquiry?.enqDate, quotation.enqId]);

  const quotationSupplierLabel = React.useMemo(() => {
    const s = quotation.supplier;
    if (!s) return null;
    const name = (s.name ?? '').trim();
    if (name) return name;
    const code = (s.code ?? '').trim();
    return code || null;
  }, [quotation.supplier]);

  const showRfContextBanner =
    Boolean(quotationCaseCaption) ||
    Boolean(quotationEnquiryCaption) ||
    Boolean(quotationSupplierLabel) ||
    Boolean((formData.supplierRef || '').trim());

  const portalMeta = React.useMemo(() => {
    const submittedAt = quotation.enquiry?.rfqPublicSubmittedAt ?? null;
    const isSubmitted = Boolean(submittedAt);
    return {
      isSubmitted,
      /** Staff-facing portal timestamps (ISO strings from API). */
      rfqPortalLastOpenedAt: quotation.rfqPortalLastOpenedAt ?? null,
      rfqPortalLastSavedAt: quotation.rfqPortalLastSavedAt ?? null,
      submittedAt,
    };
  }, [
    quotation.enquiry?.rfqPublicSubmittedAt,
    quotation.rfqPortalLastOpenedAt,
    quotation.rfqPortalLastSavedAt,
  ]);

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <Card
        variant="outlined"
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5 sm:py-3.5',
          'border shadow-sm',
          colors.border,
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('-ml-2 gap-1.5 font-medium', colors.text)}
          disabled={draftBusy || submitBusy}
          onClick={onBack}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          Back
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={draftBusy || submitBusy}
            loading={draftBusy}
            onClick={() => void persistDraft()}
          >
            Save quotation as draft
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={draftBusy || submitBusy}
            loading={submitBusy}
            onClick={() => void finalizeOffer()}
          >
            Submit offer
          </Button>
        </div>
      </Card>

      <div
        role="region"
        aria-labelledby="supplier-portal-heading"
        className={cn(
          'overflow-hidden rounded-2xl border shadow-sm ring-1 ring-black/5 dark:ring-white/10',
          colors.border,
          'bg-white dark:bg-slate-950/50',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4',
            colors.border,
            'bg-gradient-to-br from-indigo-50/95 via-white to-slate-50/90 dark:from-indigo-950/35 dark:via-slate-950/80 dark:to-slate-950',
          )}
        >
          <div className="flex min-w-0 items-start gap-3.5">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl shadow-inner',
                'bg-indigo-600 text-white shadow-indigo-900/20 dark:bg-indigo-500 dark:shadow-none',
              )}
              aria-hidden
            >
              <EyeIcon className="h-6 w-6 opacity-95" />
            </div>
            <div className="min-w-0 pt-0.5">
              <h2
                id="supplier-portal-heading"
                className={cn('text-lg font-semibold leading-tight tracking-tight', colors.text)}
              >
                Supplier portal
              </h2>
              <p className={cn('mt-1 max-w-xl text-sm leading-snug', colors.textSecondary)}>
                Offer status and timestamps for this secure RFQ session (PIN access).
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            <span className={cn('text-xs font-medium uppercase tracking-wider', colors.textSecondary)}>Status</span>
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-semibold shadow-sm',
                portalMeta.isSubmitted
                  ? 'border-emerald-400/50 bg-emerald-500 text-white shadow-emerald-900/25 dark:border-emerald-500/60 dark:bg-emerald-600'
                  : 'border-amber-400/45 bg-amber-500 text-amber-950 shadow-amber-900/15 dark:border-amber-500/50 dark:bg-amber-400 dark:text-amber-950',
              )}
            >
              {portalMeta.isSubmitted ? 'Submitted' : 'Draft'}
            </span>
          </div>
        </div>

        <div className="grid divide-y divide-slate-200/90 dark:divide-slate-700/90 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <div
            className={cn(
              'flex gap-3 px-4 py-4 sm:px-5',
              'bg-slate-50/70 dark:bg-slate-900/30',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                'bg-white text-indigo-600 shadow-sm dark:bg-slate-800 dark:text-indigo-400',
              )}
              title="When your offer was finalized via Submit offer"
            >
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className={cn('text-[0.65rem] font-semibold uppercase tracking-wider', colors.textSecondary)}>
                Submitted date
              </p>
              <p className={cn('mt-1 text-sm font-medium tabular-nums leading-snug', colors.text)}>
                {portalMeta.submittedAt ? formatDateTimeGlobal(portalMeta.submittedAt) : 'Not submitted yet'}
              </p>
            </div>
          </div>

          <div className={cn('flex gap-3 px-4 py-4 sm:px-5', 'bg-white dark:bg-transparent')}>
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                'bg-white text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300',
              )}
              title="rfqPortalLastOpenedAt — opened RFQ link (PIN verified)"
            >
              <ClockIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className={cn('text-[0.65rem] font-semibold uppercase tracking-wider', colors.textSecondary)}>
                Last login
              </p>
              <p className={cn('mt-1 text-sm font-medium tabular-nums leading-snug', colors.text)}>
                {portalMeta.rfqPortalLastOpenedAt ? formatDateTimeGlobal(portalMeta.rfqPortalLastOpenedAt) : '—'}
              </p>
            </div>
          </div>

          <div
            className={cn(
              'flex gap-3 px-4 py-4 sm:px-5',
              'bg-slate-50/70 dark:bg-slate-900/30',
            )}
          >
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                'bg-white text-slate-600 shadow-sm dark:bg-slate-800 dark:text-slate-300',
              )}
              title="rfqPortalLastSavedAt — draft or submit save"
            >
              <DocumentTextIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className={cn('text-[0.65rem] font-semibold uppercase tracking-wider', colors.textSecondary)}>
                Last saved
              </p>
              <p className={cn('mt-1 text-sm font-medium tabular-nums leading-snug', colors.text)}>
                {portalMeta.rfqPortalLastSavedAt ? formatDateTimeGlobal(portalMeta.rfqPortalLastSavedAt) : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showRfContextBanner ? (
        <Callout
          variant="success"
          title={quotationCaseCaption ? 'Linked supply case' : 'RFQ context'}
        >
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {quotationCaseCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm',
                      'border-green-200/90 bg-green-100/70 text-green-950 dark:border-green-700/60 dark:bg-green-900/40 dark:text-green-100',
                    )}
                  >
                    {quotationCaseCaption}
                  </span>
                ) : null}
                {quotationEnquiryCaption ? (
                  <span
                    className={cn(
                      'inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium',
                      'border-indigo-200/90 bg-indigo-50/90 text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-200',
                    )}
                  >
                    {quotationEnquiryCaption}
                  </span>
                ) : null}
                {quotationSupplierLabel || (formData.supplierRef || '').trim() ? (
                  <span
                    className={cn(
                      'inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border px-2.5 py-1 text-sm',
                      'border-teal-200/90 bg-teal-50/85 text-teal-950 dark:border-teal-700/55 dark:bg-teal-950/40 dark:text-teal-100',
                    )}
                  >
                    {quotationSupplierLabel ? (
                      <>
                        <span className="text-teal-800/90 dark:text-teal-200/90">Supplier</span>
                        <span className="font-medium">{quotationSupplierLabel}</span>
                      </>
                    ) : null}
                    {(formData.supplierRef || '').trim() ? (
                      <>
                        {quotationSupplierLabel ? (
                          <span className="select-none text-teal-700/45 dark:text-teal-300/35" aria-hidden>
                            ·
                          </span>
                        ) : null}
                        <span className="text-teal-800/90 dark:text-teal-200/90">Ref</span>
                        <span className="font-semibold tabular-nums">{(formData.supplierRef || '').trim()}</span>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Quotation details"
            description="Dates, currency, and commercial terms."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-3">
                <Label htmlFor="portal-quo-date">Quotation date</Label>
                <DateInput
                  id="portal-quo-date"
                  value={formData.quoDate}
                  onChange={(e) => setFormData((s) => ({ ...s, quoDate: e.target.value }))}
                  placeholder="Select date"
                  showFormatHint={false}
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="portal-quo-ref">Reference</Label>
                <TextInput
                  id="portal-quo-ref"
                  value={formData.ref}
                  onChange={(e) => setFormData((s) => ({ ...s, ref: e.target.value }))}
                  placeholder="Reference"
                  disabled
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="portal-quo-currency">Currency</Label>
                <Select
                  id="portal-quo-currency"
                  value={formData.currencyId}
                  onChange={(e) => setFormData((s) => ({ ...s, currencyId: e.target.value }))}
                  options={currencyOptions}
                  showClearButton={false}
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="portal-quo-rate">Rate</Label>
                <NumberInput
                  id="portal-quo-rate"
                  value={formData.rate}
                  onChange={(e) =>
                    setFormData((s) => ({ ...s, rate: e.target.value == null ? '' : String(e.target.value) }))
                  }
                  showClearButton={false}
                  placeholder="Rate"
                  className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="portal-quo-supplier-ref">Supplier ref</Label>
                <TextInput
                  id="portal-quo-supplier-ref"
                  value={formData.supplierRef}
                  onChange={(e) => setFormData((s) => ({ ...s, supplierRef: e.target.value }))}
                  placeholder="Supplier reference"
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-9">
                <Label htmlFor="portal-quo-payment-terms">Payment terms</Label>
                <TextInput
                  id="portal-quo-payment-terms"
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData((s) => ({ ...s, paymentTerms: e.target.value }))}
                  placeholder="Payment terms"
                  className="h-10 min-h-[2.5rem]"
                />
              </div>
              <div className="md:col-span-12">
                <Label htmlFor="portal-supplier-notes">Supplier notes</Label>
                <Textarea
                  id="portal-supplier-notes"
                  value={formData.supplierNotes}
                  onChange={(e) => setFormData((s) => ({ ...s, supplierNotes: e.target.value }))}
                  rows={4}
                  showClearButton={false}
                  placeholder="Notes for your offer (visible on the quotation)"
                  className="min-h-[96px]"
                />
              </div>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Expenses"
            description="Additional charges on this quotation."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                title="Add expense"
                aria-label="Add expense"
                onClick={() => setExpenses((prev) => [...prev, newExpense()])}
              >
                <PlusIcon className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="min-w-[10rem] px-2 py-2 text-left text-xs font-medium">Description</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">Value</th>
                    <th className="w-28 px-2 py-2 text-left text-xs font-medium">VAT</th>
                    <th className="px-2 py-2 text-right text-xs font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((row) => (
                    <tr key={row.key} className="align-top">
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          value={row.description}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, description: e.target.value } : r)),
                            )
                          }
                          showClearButton={false}
                          placeholder="Description"
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.value}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) =>
                                r.key === row.key ? { ...r, value: e.target.value == null ? '' : String(e.target.value) } : r,
                              ),
                            )
                          }
                          showClearButton={false}
                          placeholder="Value"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={row.vat}
                          onChange={(e) =>
                            setExpenses((prev) =>
                              prev.map((r) =>
                                r.key === row.key ? { ...r, vat: e.target.value == null ? '' : String(e.target.value) } : r,
                              ),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() =>
                            setExpenses((prev) =>
                              prev.length > 1 ? prev.filter((r) => r.key !== row.key) : [newExpense()],
                            )
                          }
                        >
                          <TrashIcon className="h-5 w-5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Lines"
            description="RFQ lines — edit quantities, prices, discounts, and VAT."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item Discount (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkDiscountPct}
                    onChange={(e) => setBulkDiscountPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkDiscountPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const discountValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            discount: discountValue,
                            lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
                <div className={cn('flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm', colors.border)}>
                  <span className={cn('text-xs font-medium', colors.textSecondary)}>Apply Item VAT (%)</span>
                  <NumberInput
                    size="sm"
                    value={bulkVatPct}
                    onChange={(e) => setBulkVatPct(e.target.value == null ? '' : String(e.target.value))}
                    placeholder="%"
                    showClearButton={false}
                    className="h-9 min-h-[2.25rem] w-24 box-border py-1.5 leading-normal"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const pct = Number(bulkVatPct);
                      if (!Number.isFinite(pct)) return;
                      setLines((prev) =>
                        prev.map((r) => {
                          const unit = Number(r.unitTotal);
                          const vatValue = Number.isFinite(unit) ? toFixed2String((unit * pct) / 100) : '';
                          return {
                            ...r,
                            vat: vatValue,
                            lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                          };
                        }),
                      );
                    }}
                    disabled={!lines.length}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={cn('border-b', colors.border)}>
                    <th className="w-[10rem] min-w-[8rem] px-2 py-2 text-left text-xs font-medium">Level 1</th>
                    <th className="min-w-[12rem] px-2 py-2 text-left text-xs font-medium">Item</th>
                    <th className="w-[6.5rem] min-w-[6.5rem] px-2 py-2 text-right text-xs font-medium">Unit</th>
                    <th className="w-24 px-2 py-2 text-left text-xs font-medium">Req. qty</th>
                    <th className="w-24 px-2 py-2 text-left text-xs font-medium">Qty</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Price</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Unit Total</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Discount</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">VAT</th>
                    <th className="w-24 px-2 py-2 text-right text-xs font-medium">Total</th>
                    <th className="w-28 px-2 py-2 text-right text-xs font-medium">{currencyTotalCaption}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.key} className="align-top">
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          readOnly
                          value={line.level1Filter}
                          onChange={() => {}}
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2">
                        <TextInput
                          size="sm"
                          readOnly
                          value={formatQuotationItemLabel(line.itemVcode, line.itemDescr, line.unitId, units)}
                          onChange={() => {}}
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem] min-w-[12rem]"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <TextInput
                          size="sm"
                          readOnly
                          value={
                            line.unitId
                              ? units.find((u) => String(u.id) === line.unitId)?.ucode?.trim() || ''
                              : ''
                          }
                          onChange={() => {}}
                          showClearButton={false}
                          className="h-10 min-h-[2.5rem]"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          readOnly
                          showClearButton={false}
                          value={line.requisitionQty}
                          placeholder="Req."
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2">
                        <NumberInput
                          size="sm"
                          value={line.qty}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const qtyValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(qtyValue, r.unitPrice);
                                return {
                                  ...r,
                                  qty: qtyValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Qty"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitPrice}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const priceValue = e.target.value == null ? '' : String(e.target.value);
                                const unitTotal = computeUnitTotal(r.qty, priceValue);
                                return {
                                  ...r,
                                  unitPrice: priceValue,
                                  unitTotal,
                                  lineTotal: computeFinalTotal(unitTotal, r.discount, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Price"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.unitTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Unit Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.discount}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const discountValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  discount: discountValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, discountValue, r.vat),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="Discount"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.vat}
                          onChange={(e) =>
                            setLines((prev) =>
                              prev.map((r) => {
                                if (r.key !== line.key) return r;
                                const vatValue = e.target.value == null ? '' : String(e.target.value);
                                return {
                                  ...r,
                                  vat: vatValue,
                                  lineTotal: computeFinalTotal(r.unitTotal, r.discount, vatValue),
                                };
                              }),
                            )
                          }
                          showClearButton={false}
                          placeholder="VAT"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={line.lineTotal}
                          disabled
                          showClearButton={false}
                          placeholder="Total"
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <NumberInput
                          size="sm"
                          value={computeCurrencyTotal(line.lineTotal, formData.rate)}
                          disabled
                          showClearButton={false}
                          placeholder={currencyTotalCaption}
                          className="h-10 min-h-[2.5rem] box-border py-2 leading-normal text-right"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={cn('border-t', colors.border)}>
                    <td colSpan={6} className={cn('px-2 py-2 text-right text-xs font-semibold', colors.textSecondary)}>
                      SUM
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>
                        Unit Total SUM
                      </div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.unitTotal)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>
                        Discount SUM
                      </div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.discount)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>VAT SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.vat)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>TOTAL SUM</div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>{fmt2(lineSums.total)}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className={cn('text-[0.7rem] font-semibold uppercase text-right', colors.textSecondary)}>
                        {currencyTotalCaption} SUM
                      </div>
                      <div className={cn('text-sm font-semibold tabular-nums text-right', colors.text)}>
                        {fmt2(lineSums.currencyTotal)}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </AccordionItem>
        </Accordion>

        {lineRollCount || expenseRollCount ? (
          <div
            className={cn(
              'mt-4 rounded-lg border px-4 py-3 text-sm sm:px-5',
              colors.border,
              'bg-slate-50/90 dark:bg-slate-800/30',
            )}
          >
            <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textSecondary)}>Quotation roll-up</h3>
            <dl className="mt-2 space-y-1.5 sm:grid sm:grid-cols-1 sm:gap-2 sm:space-y-0">
              {lineRollCount ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Lines subtotal (quotation currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(lineSums.total)}</dd>
                </div>
              ) : null}
              {expenseRollCount ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4">
                  <dt className="text-slate-600 dark:text-slate-400">Expenses (value + VAT, quotation currency)</dt>
                  <dd className="font-medium tabular-nums">{fmt2(expenseSums.value + expenseSums.vat)}</dd>
                </div>
              ) : null}
              {lineRollCount && expenseRollCount ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-dashed pt-2 sm:gap-4">
                  <dt className="font-medium text-slate-700 dark:text-slate-300">Combined (quotation currency)</dt>
                  <dd className="font-semibold tabular-nums">{fmt2(quoteCcySubtotal)}</dd>
                </div>
              ) : null}
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-t pt-2 sm:gap-4">
                <dt className="font-medium text-slate-800 dark:text-slate-200">
                  Grand total ({currencyTotalCaption}, lines + expenses)
                </dt>
                <dd className="text-base font-bold tabular-nums text-indigo-700 dark:text-indigo-300">
                  {fmt2(grandCurrencyTotal)}
                </dd>
              </div>
            </dl>
            <p className={cn('mt-2 text-xs', colors.textSecondary)}>
              Line totals: line total × rate (same as edit). Expenses: (value + VAT) × rate.
            </p>
          </div>
        ) : null}
      </Card>
    </form>
  );
};

export default PublicRfqQuotationEditor;
